import * as React from "react";
import { capitaliseSentence } from "./capitaliseSentence";
import { ChevronDown, Plus, Search, XCircle } from "lucide-react";
import { cn } from "./utils";
import { Badge } from "./badge";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./dialog";
import { Label } from "./label";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { ScrollArea } from "./scroll-area";

export interface MultiSelectOption {
    label: string;
    value: string;
    /** Secondary line shown in the popover list (e.g. start date). */
    description?: string;
    /** Extra text included in popover search (e.g. uuid). */
    searchText?: string;
    /** Optional leading icon in the list and selected chips. */
    icon?: React.ReactNode;
}

export interface MultiSelectAddOptionActions {
    close: () => void;
}

export interface MultiSelectProps {
    options: MultiSelectOption[];
    onValueChange: (value: string[]) => void;
    defaultValue?: string[];
    value?: string[];
    placeholder?: string;
    className?: string;
    popoverContentClassName?: string;
    maxCount?: number;
    disabled?: boolean;
    /** Renders in a dialog opened from the footer Add button (ComboBox-style). */
    addOption?: React.ReactNode | ((actions: MultiSelectAddOptionActions) => React.ReactNode);
    addOptionTitle?: string;
    addOptionTriggerLabel?: string;
    /** Custom chip content for a selected value (e.g. rich template details). */
    renderSelectedBadge?: (value: string) => React.ReactNode;
    /** Accessible label for remove buttons when `renderSelectedBadge` is used. */
    getSelectedAriaLabel?: (value: string) => string;
    /** Selected values that cannot be removed (e.g. template-required grants). */
    lockedValues?: string[];
    /** Optional test id on the combobox trigger. */
    "data-testid"?: string;
}

export const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
    (
        {
            options,
            onValueChange,
            defaultValue = [],
            value: controlledValue,
            placeholder = "Select options",
            className,
            popoverContentClassName,
            maxCount: _maxCount,
            disabled = false,
            addOption,
            addOptionTitle = "Add an option",
            addOptionTriggerLabel = "Add",
            renderSelectedBadge,
            getSelectedAriaLabel,
            lockedValues = [],
            "data-testid": dataTestId,
        },
        ref
    ) => {
        const selectAllId = React.useId();
        const listPanelId = React.useId();
        const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue);
        const [open, setOpen] = React.useState(false);
        const [addOptionOpen, setAddOptionOpen] = React.useState(false);
        const [search, setSearch] = React.useState("");

        const isControlled = controlledValue !== undefined;
        const selectedValues = isControlled ? controlledValue : internalValue;
        const lockedSet = React.useMemo(() => new Set(lockedValues), [lockedValues]);

        const prevDefaultRef = React.useRef(defaultValue);
        React.useEffect(() => {
            if (isControlled) return;
            if (
                prevDefaultRef.current.length !== defaultValue.length ||
                !prevDefaultRef.current.every((v, i) => v === defaultValue[i])
            ) {
                setInternalValue(defaultValue);
                prevDefaultRef.current = defaultValue;
            }
        }, [defaultValue, isControlled]);

        React.useEffect(() => {
            if (!open) setSearch("");
        }, [open]);

        const safeOptions = React.useMemo(() => options ?? [], [options]);

        const filteredOptions = React.useMemo(() => {
            if (!search) return safeOptions;
            const q = search.toLowerCase();
            return safeOptions.filter((o) => {
                const haystack = [o.label, o.value, o.description, o.searchText]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return haystack.includes(q);
            });
        }, [safeOptions, search]);

        const updateValue = React.useCallback(
            (next: string[]) => {
                const withLocks = [...new Set([...lockedValues, ...next])];
                if (!isControlled) setInternalValue(withLocks);
                onValueChange(withLocks);
            },
            [isControlled, lockedValues, onValueChange]
        );

        const toggleOption = (optionValue: string) => {
            if (disabled) return;
            if (lockedSet.has(optionValue) && selectedValues.includes(optionValue)) return;
            const next = selectedValues.includes(optionValue)
                ? selectedValues.filter((v) => v !== optionValue)
                : [...selectedValues, optionValue];
            updateValue(next);
        };

        const removeValue = (value: string) => {
            if (disabled || lockedSet.has(value)) return;
            updateValue(selectedValues.filter((v) => v !== value));
        };

        const handleClear = () => {
            if (disabled) return;
            updateValue([...lockedValues]);
        };

        const handleSelectAll = (checked: boolean) => {
            if (disabled) return;
            updateValue(checked ? safeOptions.map((o) => o.value) : [...lockedValues]);
        };

        const getOption = (value: string) => safeOptions.find((o) => o.value === value);
        const getLabel = (value: string) => getOption(value)?.label ?? value;

        const isAllSelected = safeOptions.length > 0 && selectedValues.length === safeOptions.length;
        const isIndeterminate = selectedValues.length > 0 && selectedValues.length < safeOptions.length;
        const showSelectAll = safeOptions.length > 1;

        const addOptionContent =
            typeof addOption === "function" ? addOption({ close: () => setAddOptionOpen(false) }) : addOption;

        return (
            <Popover open={open} onOpenChange={setOpen} modal={false}>
                <PopoverTrigger asChild>
                    <div
                        ref={ref}
                        role="combobox"
                        aria-expanded={open}
                        aria-controls={open ? listPanelId : undefined}
                        data-testid={dataTestId}
                        className={cn(
                            "flex justify-between items-center gap-2 bg-card hover:bg-card/80 px-3 py-2 border border-slate-300 dark:border-slate-500 rounded-md w-full min-h-10 cursor-pointer",
                            disabled && "opacity-50 cursor-not-allowed",
                            className
                        )}
                        aria-label={placeholder}
                    >
                        <div className="flex flex-wrap flex-1 items-center gap-2 min-w-0">
                            {selectedValues.map((value) => {
                                const option = getOption(value);
                                const rawLabel = getSelectedAriaLabel?.(value) ?? getLabel(value);
                                return (
                                    <Badge
                                        key={value}
                                        variant="secondary"
                                        className={cn(
                                            "flex gap-1.5 py-1 max-w-full hover:scale-105 transition-all hover:-translate-y-0.5 duration-300 ease-in-out",
                                            renderSelectedBadge ? "items-start py-1.5" : "items-center"
                                        )}
                                    >
                                        {renderSelectedBadge ? (
                                            <div className="flex-1 min-w-0">{renderSelectedBadge(value)}</div>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 min-w-0">
                                                {option?.icon ? (
                                                    <span className="[&_svg]:w-6 [&_svg]:h-6 shrink-0">
                                                        {option.icon}
                                                    </span>
                                                ) : null}
                                                <span className="truncate capitalize">
                                                    {option?.icon
                                                        ? rawLabel
                                                        : capitaliseSentence(String(rawLabel), "-")}
                                                </span>
                                            </span>
                                        )}
                                        {lockedSet.has(value) ? null : (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeValue(value);
                                                }}
                                                className="hover:bg-indigo-200 dark:hover:bg-indigo-800 ml-0.5 p-0.5 rounded-full cursor-pointer shrink-0"
                                                aria-label={`Remove ${rawLabel}`}
                                            >
                                                <XCircle className="w-4 h-4" />
                                            </button>
                                        )}
                                    </Badge>
                                );
                            })}
                            {selectedValues.length === 0 && (
                                <span className="text-muted-foreground text-sm truncate">{placeholder}</span>
                            )}
                        </div>
                        <ChevronDown className="opacity-75 w-4 h-4 shrink-0" />
                    </div>
                </PopoverTrigger>
                <PopoverContent
                    id={listPanelId}
                    className={cn("p-0 w-[var(--radix-popover-trigger-width)]", popoverContentClassName)}
                    align="start"
                >
                    {showSelectAll && (
                        <>
                            <div className="flex items-center px-2 border-b text-muted-foreground">
                                <Search className="ml-2 w-4 h-4 shrink-0" />
                                <input
                                    placeholder="Filter options..."
                                    className="flex-1 bg-transparent p-2 border-0 outline-none focus-visible:outline-none ring-0 focus-visible:ring-0 text-sm"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            {!search && (
                                <div className="flex items-center px-4 py-2 border-b">
                                    <Checkbox
                                        checked={isAllSelected || isIndeterminate}
                                        onCheckedChange={handleSelectAll}
                                        id={selectAllId}
                                        className={cn("mr-2", isIndeterminate && "opacity-50")}
                                    />
                                    <Label htmlFor={selectAllId}>
                                        {isAllSelected
                                            ? "Deselect All"
                                            : isIndeterminate
                                              ? "Some selected"
                                              : "Select All"}
                                    </Label>
                                </div>
                            )}
                        </>
                    )}
                    <ScrollArea className="h-[300px]">
                        <ul className="flex flex-col px-2 py-1">
                            {filteredOptions.map((option) => {
                                const isSelected = selectedValues.includes(option.value);
                                return (
                                    <li
                                        key={option.value}
                                        data-testid={dataTestId ? `${dataTestId}-option-${option.value}` : undefined}
                                        className="flex items-center gap-2 hover:bg-accent p-2 rounded-sm cursor-pointer"
                                        onClick={() => toggleOption(option.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                toggleOption(option.value);
                                            }
                                        }}
                                        tabIndex={0}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onCheckedChange={() => toggleOption(option.value)}
                                            tabIndex={-1}
                                        />
                                        {option.icon ? (
                                            <span className="[&_svg]:w-[1.875rem] [&_svg]:h-[1.875rem] shrink-0">
                                                {option.icon}
                                            </span>
                                        ) : null}
                                        <div className="flex flex-col gap-0.5 min-w-0">
                                            <Label className="font-normal cursor-pointer">{option.label}</Label>
                                            {option.description && (
                                                <span className="text-muted-foreground text-xs truncate">
                                                    {option.description}
                                                </span>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                            {filteredOptions.length === 0 && (
                                <li className="py-4 text-muted-foreground text-sm text-center">No results found.</li>
                            )}
                        </ul>
                    </ScrollArea>
                    <div className="flex flex-wrap gap-2 p-3 border-t">
                        <Button type="button" variant="outline" size="sm" onClick={handleClear}>
                            Clear
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                            Close
                        </Button>
                        {addOptionContent ? (
                            <Dialog open={addOptionOpen} onOpenChange={setAddOptionOpen}>
                                <DialogTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="flex items-center gap-1.5"
                                        disabled={disabled}
                                    >
                                        <Plus className="w-4 h-4" />
                                        {addOptionTriggerLabel}
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>{addOptionTitle}</DialogTitle>
                                    </DialogHeader>
                                    {addOptionContent}
                                </DialogContent>
                            </Dialog>
                        ) : null}
                    </div>
                </PopoverContent>
            </Popover>
        );
    }
);

MultiSelect.displayName = "MultiSelect";
